import { Badge } from '@talentmesh/ui/components/badge';
import { SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu } from '@talentmesh/ui/components/sidebar';
import { Skeleton } from '@talentmesh/ui/components/skeleton';
import { Inbox, UserRoundCheck, UserRoundX } from 'lucide-react';
import { useCallback } from 'react';
import { generatePath } from 'react-router';
import { ProfileDetailsSidebarProps } from './CandidatesOverviewNavigationSidebar';
import LineMenuItem from './LineMenuItem';
import { useRecruitmentId } from '@/Hooks';
import useCompanyId from '@/Hooks/UseCompanyId';
import { CandidateTabs } from '@/Models/CandidateOverview';
import useApplicantAssessmentCounters from '@/Pages/Candidates/AiSearch/api/useApplicantAssessmentCounters';
import RoutePath from '@/Routing/RoutePath';
import UIStrings from '@/Utils/UIStrings';

type ApplicantsListNavGroupProps = Pick<
    ProfileDetailsSidebarProps,
    'handleApplicantOverviewTabChange' | 'selectedApplicantOverviewTab'
> & {
    isApplicantOverviewPage: boolean;
};

const applicantOverviewTabs: CandidateTabs[] = [CandidateTabs.Open, CandidateTabs.Hired, CandidateTabs.Rejected];

const ApplicantsListNavGroup = ({
    handleApplicantOverviewTabChange,
    selectedApplicantOverviewTab,
    isApplicantOverviewPage,
}: ApplicantsListNavGroupProps) => {
    const assessmentId = useRecruitmentId();
    const companyId = useCompanyId();
    const { data: applicantAssessmentCountersData, isLoading: isApplicantAssessmentCountersLoading } =
        useApplicantAssessmentCounters();

    const handleApplicantsListNav = (event: React.SyntheticEvent, nextTab: CandidateTabs): void => {
        if (handleApplicantOverviewTabChange) {
            handleApplicantOverviewTabChange(event, nextTab);
        }
    };

    const { totalOpen, totalHired, totalRejected } = applicantAssessmentCountersData ?? {};
    const getApplicantsListItem = useCallback(
        (tabName: CandidateTabs) => {
            switch (tabName) {
                case CandidateTabs.Open:
                    return [<Inbox key="open" />, UIStrings.Open, totalOpen ?? 0];
                case CandidateTabs.Hired:
                    return [<UserRoundCheck key="hired" />, UIStrings.Hired, totalHired ?? 0];
                case CandidateTabs.Rejected:
                    return [<UserRoundX key="rejected" />, UIStrings.Rejected, totalRejected ?? 0];
                default:
                    throw new Error(`Invalid tab name: ${tabName}`);
            }
        },
        [totalOpen, totalHired, totalRejected],
    );

    return (
        <SidebarGroup className="p-0" data-cy="applicantsList">
            <SidebarGroupLabel className="text-muted-foreground text-xs tracking-wide uppercase">
                <span className="mx-2">{UIStrings.Applicants}</span>
            </SidebarGroupLabel>
            <SidebarGroupContent>
                <SidebarMenu>
                    {applicantOverviewTabs.map((tabName) => {
                        const [icon, label, count] = getApplicantsListItem(tabName);

                        return (
                            <LineMenuItem
                                key={tabName}
                                isActive={selectedApplicantOverviewTab === tabName}
                                {...(isApplicantOverviewPage
                                    ? {
                                          buttonProps: {
                                              onClick: (e) => handleApplicantsListNav(e, tabName),
                                              className: 'cursor-pointer',
                                          },
                                      }
                                    : {
                                          to: generatePath(`${RoutePath.CandidatesOverview}?tab=${tabName}`, {
                                              assessmentId,
                                              companyId,
                                          }),
                                      })}
                            >
                                {icon}
                                {label}
                                {isApplicantAssessmentCountersLoading ? (
                                    <Skeleton className="ml-auto h-full w-[3ch] rounded-4xl" />
                                ) : (
                                    <Badge
                                        data-testid={`${tabName}Count`}
                                        variant="secondary"
                                        className="ml-auto text-inherit"
                                    >
                                        {count}
                                    </Badge>
                                )}
                            </LineMenuItem>
                        );
                    })}
                </SidebarMenu>
            </SidebarGroupContent>
        </SidebarGroup>
    );
};

export default ApplicantsListNavGroup;
